(() => {
  'use strict';

  const VERSION = '5.1.1';
  const ROOT_SELECTOR = '[data-egifts-product-cross-sell]';
  const DISPLAY_BATCH_SIZE = 12;
  const RANDOM_INITIAL_PAGES = 4;
  const DESKTOP_QUERY = '(min-width: 750px)';

  const EXCLUDED_COLLECTIONS = new Set([
    'kubki-z-nadrukiem',
    'torba-ze-zdjeciem-i-wlasnym-nadrukiem',
    'bluza-ze-zdjeciem'
  ]);

  const TECHNICAL_HANDLES = new Set([
    'dodatkowy-napis',
    'dodatowe-zdjecie-na-tyl-koszulki',
    'dwie-twarze-bokserki',
    'kubek-z-tym-samym-wzorem',
    'koszulka-z-tym-samym-wzorem',
    'magnes-z-otwieraczem-i-wlasnym-tekstem',
    'magnes-ze-zdjeciem',
    'nadruk-na-calych-bokserkach-przod-tyl',
    'poduszka-z-tym-samym-wzorem',
    'zapakuj-na-prezent',
    'zdjecie-ai'
  ]);

  const CATEGORY_COLLECTIONS = {
    newest: ['all'],
    hoodie: ['bluza-ze-zdjeciem'],
    funny_shirt: ['smieszne-koszulki-z-nadrukiem'],
    group_trip: ['koszulki-na-wyjazd'],
    vest: ['kamizelki-odblaskowe-z-nadrukiem-smieszne-i-personalizowane-kamizelki-na-prezent'],
    bestseller: ['bestsellery'],
    boxers: ['bokserki-z-twarza'],
    panties: ['majtki-z-twarza-i-zdjeciem'],
    socks: ['skarpetki-ze-zdjeciem'],
    mousepad: ['podkladka-pod-mysz-z-twarza'],
    shirt: [
      'smieszne-koszulki-z-nadrukiem',
      'koszulki-z-twoim-zdjeciem',
      'koszulki-i-love-my-boyfriend',
      'koszulki-i-love-my-girlfriend',
      'koszulki-dla-mamy',
      'koszulki-dla-taty',
      'koszulki-dla-rodziny',
      'koszulki-dla-par'
    ],
    mug: ['kubek-z-twoim-zdjeciem'],
    pillow: ['poduszki'],
    pet: ['zwierzaki-prezenty-z-pupilem']
  };

  if (window.EgiftsProductCrossSell?.version === VERSION) {
    window.EgiftsProductCrossSell.initAll();
    return;
  }

  const liveRoots = new Map();
  const observedRoots = new WeakSet();
  const collectionCache = new Map();
  const productDataCache = new Map();
  let excludedHandlesPromise = null;

  const nativeState = {
    fetchBridge: null,
    xhrBridgeInstalled: false,
    active: false,
    parentProductId: '',
    targetGid: '',
    expiresAt: 0,
    independentAddUntil: 0,
    opening: false,
    artwork: '',
    artworkTimer: null,
    targetProductId: '',
    targetTitle: '',
    targetRoot: null,
    baselineCustomizations: new Set(),
    persistPromise: null
  };
  const media = window.matchMedia(DESKTOP_QUERY);

  const route = (path) => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${String(path || '').replace(/^\//, '')}`.replace(/\/{2,}/g, '/');
  };

  const parseJsonScript = (scope, selector, fallback) => {
    try {
      return JSON.parse(scope.querySelector(selector)?.textContent || '');
    } catch (error) {
      return fallback;
    }
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const shuffleArray = (items) => {
    const copy = Array.from(items || []);
    for (let index = copy.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [copy[index], copy[randomIndex]] = [copy[randomIndex], copy[index]];
    }
    return copy;
  };

  const uniqueByHandle = (products) => {
    const unique = new Map();
    (products || []).forEach((product) => {
      if (product?.handle && !unique.has(product.handle)) unique.set(product.handle, product);
    });
    return [...unique.values()];
  };

  const productHandle = (product) => String(
    product?.handle || product?.url?.match(/\/products\/([^/?#]+)/)?.[1] || ''
  ).trim();

  const productImage = (product) => {
    const image = product?.image || product?.featured_image || product?.featuredImage?.url || product?.images?.[0];
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  };

  const moneyToCents = (value) => {
    if (typeof value === 'number') return Number.isInteger(value) ? value : Math.round(value * 100);
    const clean = String(value ?? '').replace(/[^0-9,.-]/g, '').replace(',', '.');
    const number = Number(clean);
    if (!Number.isFinite(number)) return 0;
    return clean.includes('.') ? Math.round(number * 100) : Math.round(number);
  };

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'PLN',
        minimumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2).replace('.', ',')} zł`;
    }
  };

  const cleanProduct = (product, sourceCollection = '', sourceOrder = 0) => {
    const handle = productHandle(product);
    if (!handle) return null;

    return {
      id: String(product.id || ''),
      handle,
      title: String(product.title || ''),
      personalizer: String(product.personalizer || ''),
      variantId: String(product.variant_id || product.variantId || product.variants?.[0]?.id || ''),
      directAdd: product.direct_add === true || product.directAdd === true,
      available: product.available !== false,
      image: productImage(product),
      price: moneyToCents(product.price ?? product.price_min),
      compareAtPrice: moneyToCents(product.compare_at_price ?? product.compare_at_price_min),
      url: String(product.url || route(`products/${handle}`)),
      sourceCollection: String(product.collection_handle || sourceCollection || ''),
      sourceOrder
    };
  };

  const fetchCollection = (handle, page = 1, sortBy = 'best-selling') => {
    if (!handle) return Promise.resolve([]);
    const cacheKey = `${handle}:${page}:${sortBy}`;
    if (collectionCache.has(cacheKey)) return collectionCache.get(cacheKey);

    const params = new URLSearchParams({
      sort_by: sortBy,
      section_id: 'egifts-cross-sell-collection-data',
      page: String(page)
    });

    const request = fetch(`${route(`collections/${handle}`)}?${params.toString()}`, {
      headers: { Accept: 'text/html' },
      cache: 'no-store'
    })
      .then((response) => response.ok ? response.text() : '')
      .then((text) => {
        if (!text) return [];
        const html = new DOMParser().parseFromString(text, 'text/html');
        const data = parseJsonScript(html, '[data-egifts-cross-sell-collection-data]', null);
        const products = Array.isArray(data?.products) ? data.products : [];
        products._egiftsPages = Number(data?.pagination?.pages || 1);
        return products;
      })
      .catch(() => []);

    collectionCache.set(cacheKey, request);
    return request;
  };

  const fetchWholeCollection = async (handle, sortBy = 'title-ascending') => {
    const first = await fetchCollection(handle, 1, sortBy);
    const pages = Math.max(1, Number(first?._egiftsPages || 1));
    if (pages <= 1) return first;
    const rest = await Promise.all(
      Array.from({ length: pages - 1 }, (_, index) => fetchCollection(handle, index + 2, sortBy))
    );
    return [first, ...rest].flat();
  };

  const loadExcludedHandles = () => {
    if (excludedHandlesPromise) return excludedHandlesPromise;
    excludedHandlesPromise = Promise.all(
      [...EXCLUDED_COLLECTIONS].map((handle) => fetchWholeCollection(handle, 'title-ascending'))
    ).then((groups) => {
      const result = new Set();
      groups.flat().forEach((product) => {
        const handle = productHandle(product);
        if (handle) result.add(handle);
      });
      return result;
    }).catch(() => new Set());
    return excludedHandlesPromise;
  };

  const currentHandle = (root) => String(root?.dataset.currentProductHandle || '').trim();

  const isBlockedCandidate = (root, product, excludedHandles = null) => {
    if (!product?.handle || !product.available || !product.image || product.price <= 0) return true;
    if (product.handle === currentHandle(root)) return true;
    if (TECHNICAL_HANDLES.has(product.handle)) return true;
    if (excludedHandles?.has(product.handle)) return true;
    return false;
  };

  const filterProducts = (root, products, excludedHandles = null, sourceCollection = '') => {
    return uniqueByHandle((products || []).map((product, index) => cleanProduct(product, sourceCollection, index)))
      .filter((product) => !isBlockedCandidate(root, product, excludedHandles));
  };

  const ensureStates = (root) => {
    if (!(root._egiftsProductCrossSellStates instanceof Map)) root._egiftsProductCrossSellStates = new Map();
    return root._egiftsProductCrossSellStates;
  };

  const setPool = (root, key, products, mode = 'random') => {
    const states = ensureStates(root);
    states.set(key, {
      products: uniqueByHandle(products),
      shown: new Set(),
      lastBatch: new Set(),
      mode
    });
  };

  const activateCategory = (root, key) => {
    root._egiftsProductCrossSellActive = key;
    root.querySelectorAll('[data-egifts-product-cross-sell-category]').forEach((button) => {
      const active = button.dataset.egiftsProductCrossSellCategory === key;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    updateShuffleButton(root);
  };

  const setStatus = (root, message = '') => {
    const status = root.querySelector('[data-egifts-product-cross-sell-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
  };

  const showAddedToast = (root, productTitle = '') => {
    let toast = root.querySelector('[data-egifts-product-cross-sell-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'egifts-product-cross-sell__toast';
      toast.dataset.egiftsProductCrossSellToast = '1';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      root.append(toast);
    }

    const title = String(productTitle || '').trim();
    toast.textContent = title ? `Dodano do koszyka ✓ ${title}` : 'Dodano do koszyka ✓';
    toast.classList.remove('is-visible');
    void toast.offsetWidth;
    toast.classList.add('is-visible');

    window.clearTimeout(root._egiftsProductCrossSellToastTimer);
    root._egiftsProductCrossSellToastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 2200);
  };

  const updateShuffleButton = (root) => {
    const states = ensureStates(root);
    const randomButton = root.querySelector('[data-egifts-product-cross-sell-shuffle]');
    const categoryButton = root.querySelector('[data-egifts-product-cross-sell-category-shuffle]');
    const key = root._egiftsProductCrossSellActive || 'random';
    if (randomButton) randomButton.disabled = !states.get('random')?.products?.length;
    if (categoryButton) {
      categoryButton.disabled = key === 'random' || !states.get(key)?.products?.length;
      categoryButton.hidden = key === 'random';
    }
  };

  const getNextBatch = (state) => {
    if (!state?.products?.length) return [];
    const order = (items) => state.mode === 'ordered' ? Array.from(items) : shuffleArray(items);
    let available = state.products.filter((product) => !state.shown.has(product.handle));
    let batch = order(available).slice(0, DISPLAY_BATCH_SIZE);

    if (batch.length < DISPLAY_BATCH_SIZE && state.products.length > batch.length) {
      const current = new Set(batch.map((product) => product.handle));
      const previous = new Set(state.lastBatch);
      state.shown.clear();
      let refill = state.products.filter((product) => !current.has(product.handle) && !previous.has(product.handle));
      if (refill.length < DISPLAY_BATCH_SIZE - batch.length) {
        const refillHandles = new Set(refill.map((product) => product.handle));
        refill = refill.concat(state.products.filter((product) => !current.has(product.handle) && !refillHandles.has(product.handle)));
      }
      batch = batch.concat(order(refill).slice(0, DISPLAY_BATCH_SIZE - batch.length));
    }

    if (!batch.length) {
      state.shown.clear();
      let fresh = state.products.filter((product) => !state.lastBatch.has(product.handle));
      if (!fresh.length) fresh = Array.from(state.products);
      batch = order(fresh).slice(0, DISPLAY_BATCH_SIZE);
    }

    batch.forEach((product) => state.shown.add(product.handle));
    state.lastBatch = new Set(batch.map((product) => product.handle));
    return batch;
  };

  const renderCards = (root, products) => {
    const list = root.querySelector('[data-egifts-product-cross-sell-list]');
    if (!list) return;
    const active = root._egiftsProductCrossSellActive || 'random';

    list.innerHTML = (products || []).map((product) => {
      const compare = product.compareAtPrice > product.price
        ? `<s>${escapeHtml(formatMoney(product.compareAtPrice))}</s>`
        : '';
      const personalized = product.personalizer === 'teeinblue' || product.personalizer === 'customily';
      const cta = product.directAdd ? 'Do koszyka' : (personalized ? 'Personalizuj' : 'Zobacz');
      const badge = active === 'bestseller'
        ? '<span class="egifts-product-cross-sell-card__badge">BESTSELLER</span>'
        : '';

      return `
        <article class="egifts-product-cross-sell-card">
          <button
            type="button"
            class="egifts-product-cross-sell-card__button"
            data-egifts-product-cross-sell-open
            data-product-handle="${escapeHtml(product.handle)}"
            data-product-title="${escapeHtml(product.title)}"
            data-product-url="${escapeHtml(product.url)}"
            data-product-personalizer="${escapeHtml(product.personalizer)}"
            data-product-variant-id="${escapeHtml(product.variantId)}"
            data-product-direct-add="${product.directAdd ? 'true' : 'false'}"
            aria-label="${escapeHtml(cta)}: ${escapeHtml(product.title)}"
          >
            <span class="egifts-product-cross-sell-card__media">
              <img src="${escapeHtml(product.image)}" alt="" loading="lazy" width="420" height="370">
              ${badge}
            </span>
            <span class="egifts-product-cross-sell-card__body">
              <strong class="egifts-product-cross-sell-card__title">${escapeHtml(product.title)}</strong>
              <span class="egifts-product-cross-sell-card__bottom">
                <span class="egifts-product-cross-sell-card__price">${compare}${escapeHtml(formatMoney(product.price))}</span>
                <span class="egifts-product-cross-sell-card__cta" data-egifts-product-cross-sell-cta>${escapeHtml(cta)}</span>
              </span>
            </span>
          </button>
        </article>
      `;
    }).join('');
  };

  const renderActive = (root, smooth = false) => {
    const key = root._egiftsProductCrossSellActive || 'random';
    const state = ensureStates(root).get(key);
    if (!state) return;
    const batch = getNextBatch(state);
    if (!batch.length) {
      setStatus(root, 'Nie udało się teraz przygotować propozycji. Spróbuj ponownie.');
      return;
    }
    renderCards(root, batch);
    root.querySelector('[data-egifts-product-cross-sell-list]')?.scrollTo({ left: 0, behavior: smooth ? 'smooth' : 'auto' });
    setStatus(root, '');
    updateShuffleButton(root);
  };

  const prepareRandomPool = async (root) => {
    const excludedHandles = await loadExcludedHandles();
    const first = await fetchCollection('all', 1, 'title-ascending');
    const totalPages = Math.max(1, Number(first?._egiftsPages || 1));
    const pageOrder = shuffleArray(Array.from({ length: totalPages }, (_, index) => index + 1));
    const initialPages = pageOrder.slice(0, Math.min(RANDOM_INITIAL_PAGES, totalPages));
    const groups = await Promise.all(initialPages.map((page) => fetchCollection('all', page, 'title-ascending')));
    let pool = filterProducts(root, groups.flat(), excludedHandles, 'all');
    let cursor = initialPages.length;

    while (pool.length < DISPLAY_BATCH_SIZE && cursor < pageOrder.length) {
      const products = await fetchCollection('all', pageOrder[cursor], 'title-ascending');
      cursor += 1;
      pool = uniqueByHandle([...pool, ...filterProducts(root, products, excludedHandles, 'all')]);
    }

    return { pool: shuffleArray(pool), remainingPages: pageOrder.slice(cursor), excludedHandles };
  };

  const hydrateRandomPool = async (root, prepared) => {
    const pages = Array.from(prepared.remainingPages || []);
    for (let index = 0; index < pages.length; index += 3) {
      if (!document.documentElement.contains(root)) return;
      const chunk = pages.slice(index, index + 3);
      const groups = await Promise.all(chunk.map((page) => fetchCollection('all', page, 'title-ascending')));
      const products = filterProducts(root, groups.flat(), prepared.excludedHandles, 'all');
      const state = ensureStates(root).get('random');
      if (state) state.products = uniqueByHandle([...state.products, ...shuffleArray(products)]);
      updateShuffleButton(root);
      await new Promise((resolve) => window.setTimeout(resolve, 60));
    }
  };

  const roundRobin = (groups) => {
    const prepared = shuffleArray(groups.filter((group) => group.length)).map((group) => Array.from(group));
    const result = [];
    let added = true;
    while (added) {
      added = false;
      prepared.forEach((group) => {
        if (!group.length) return;
        result.push(group.shift());
        added = true;
      });
    }
    return uniqueByHandle(result);
  };

  const fetchTopCollectionPool = async (handle, maxPages = 3) => {
    const first = await fetchCollection(handle, 1, 'best-selling');
    const pages = Math.min(Math.max(1, Number(first?._egiftsPages || 1)), Math.max(1, maxPages));
    if (pages <= 1) return first;
    const rest = await Promise.all(
      Array.from({ length: pages - 1 }, (_, index) => fetchCollection(handle, index + 2, 'best-selling'))
    );
    return [first, ...rest].flat();
  };

  const loadCategory = async (root, key) => {
    if (key === 'newest') {
      const first = await fetchCollection('all', 1, 'created-descending');
      const pages = Math.min(Math.max(1, Number(first?._egiftsPages || 1)), 3);
      const rest = await Promise.all(Array.from({ length: pages - 1 }, (_, index) =>
        fetchCollection('all', index + 2, 'created-descending')));
      const products = [first, ...rest].flat();
      return filterProducts(root, products, null, 'all').slice(0, 72);
    }

    const handles = CATEGORY_COLLECTIONS[key] || [];
    if (!handles.length) return [];
    const excluded = key === 'bestseller' ? await loadExcludedHandles() : null;
    const groups = await Promise.all(handles.map(async (handle) => {
      const products = await fetchTopCollectionPool(handle, handles.length === 1 ? 3 : 1);
      return filterProducts(root, products, excluded, handle).slice(0, handles.length === 1 ? 72 : 24);
    }));
    return handles.length === 1 ? shuffleArray(groups[0] || []) : roundRobin(groups);
  };

  const scrollCarousel = (root, direction) => {
    const list = root.querySelector('[data-egifts-product-cross-sell-list]');
    const card = list?.querySelector('.egifts-product-cross-sell-card');
    if (!list || !card) return;
    const styles = window.getComputedStyle(list);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const visible = media.matches ? 4 : 2;
    list.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap) * visible, behavior: 'smooth' });
  };

  const setCardLoading = (button, loading, label) => {
    const cta = button?.querySelector('[data-egifts-product-cross-sell-cta]');
    if (cta && !cta.dataset.defaultLabel) cta.dataset.defaultLabel = cta.textContent.trim();
    if (button) {
      button.disabled = Boolean(loading);
      button.classList.toggle('is-loading', Boolean(loading));
    }
    if (cta) cta.textContent = label || cta.dataset.defaultLabel || 'Do koszyka';
  };

  const getProductData = (handle) => {
    if (productDataCache.has(handle)) return productDataCache.get(handle);

    const request = fetch(`${route(`products/${handle}`)}?section_id=egifts-cross-sell-product-data`, {
      headers: { Accept: 'text/html' },
      cache: 'no-store'
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product data HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const data = parseJsonScript(html, '[data-egifts-cross-sell-product-data]', null);
        if (!data?.productId || !data?.handle) throw new Error('Brak danych produktu.');
        return data;
      });

    productDataCache.set(handle, request);
    request.catch(() => productDataCache.delete(handle));
    return request;
  };

  const fetchCart = async () => {
    const response = await fetch(route('cart.js'), {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Cart HTTP ${response.status}`);
    return response.json();
  };

  const currentProductId = (root) => String(root?.dataset.currentProductId || '').replace(/\D/g, '');

  const cartLineProductId = (item) => String(item?.product_id || '').replace(/\D/g, '');

  const cartLineQuantity = (item) => Number(item?.quantity || 0);

  const mainLines = (root, cart) => {
    const id = currentProductId(root);
    if (!id) return [];
    return (cart?.items || []).filter((item) => cartLineProductId(item) === id);
  };

  const snapshotMainLines = (root, cart) => {
    const result = new Map();
    mainLines(root, cart).forEach((item) => result.set(String(item.key || ''), cartLineQuantity(item)));
    return result;
  };

  const primeMainBaseline = async (root) => {
    if (root._egiftsMainBaselinePromise) return root._egiftsMainBaselinePromise;
    root._egiftsMainBaselinePromise = fetchCart()
      .then((cart) => {
        root._egiftsMainBaseline = snapshotMainLines(root, cart);
        return root._egiftsMainBaseline;
      })
      .catch(() => {
        root._egiftsMainBaseline = new Map();
        return root._egiftsMainBaseline;
      });
    return root._egiftsMainBaselinePromise;
  };

  const findSessionMainLine = (root, cart) => {
    const preferredKey = String(root._egiftsMainLineKey || '');
    const lines = mainLines(root, cart);
    if (preferredKey) {
      const preferred = lines.find((item) => String(item.key || '') === preferredKey);
      if (preferred) return preferred;
    }

    const baseline = root._egiftsMainBaseline instanceof Map ? root._egiftsMainBaseline : new Map();
    return lines.find((item) => {
      const key = String(item.key || '');
      return !baseline.has(key) || cartLineQuantity(item) > Number(baseline.get(key) || 0);
    }) || null;
  };

  const findChangedMainLine = (root, beforeCart, afterCart) => {
    const before = snapshotMainLines(root, beforeCart);
    return mainLines(root, afterCart).find((item) => {
      const key = String(item.key || '');
      return !before.has(key) || cartLineQuantity(item) > Number(before.get(key) || 0);
    }) || null;
  };

  const wait = (delay) => new Promise((resolve) => window.setTimeout(resolve, delay));

  const mainSubmitButton = (root) => {
    const sectionId = String(root?.dataset.currentSectionId || '');
    return document.getElementById(`ProductSubmitButton-${sectionId}`)
      || document.querySelector(`product-form[data-section-id="${CSS.escape(sectionId)}"] [name="add"]`)
      || document.querySelector('main product-form [name="add"]');
  };

  const scrollToMainProduct = (root) => {
    const sectionId = String(root?.dataset.currentSectionId || '');
    const mainProduct = document.getElementById(`MainProduct-${sectionId}`)
      || document.querySelector(`product-info[data-section="${CSS.escape(sectionId)}"]`)
      || document.querySelector('product-info');
    if (!mainProduct) return;

    // Celowo przewijamy do POCZĄTKU prawej kolumny produktu, a nie do środka
    // całej wysokiej sekcji. Dzięki temu klient widzi tytuł, warianty,
    // personalizację i przycisk „Dodaj do koszyka” zamiast dolnych informacji.
    const target = mainProduct.querySelector?.('.product__info-wrapper')
      || mainProduct.querySelector?.('.product__info-container')
      || mainProduct;

    const header = document.querySelector('sticky-header, .header-wrapper');
    const headerRect = header?.getBoundingClientRect?.();
    const headerHeight = headerRect && headerRect.height > 0 ? Math.min(headerRect.height, 150) : 0;
    const fallbackOffset = media.matches ? 92 : 72;
    const offset = Math.max(fallbackOffset, headerHeight + 16);
    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset);

    window.scrollTo({ top, behavior: 'smooth' });
  };

  const getSessionMainLine = async (root) => {
    await primeMainBaseline(root);
    const cart = await fetchCart();
    const sessionLine = findSessionMainLine(root, cart);
    if (sessionLine) root._egiftsMainLineKey = String(sessionLine.key || '');
    return sessionLine || null;
  };

  const focusMainProductAdd = (root) => {
    scrollToMainProduct(root);
    const submit = mainSubmitButton(root);
    if (!submit) return;
    window.setTimeout(() => {
      try { submit.focus({ preventScroll: true }); } catch (error) { try { submit.focus(); } catch (ignore) {} }
      submit.classList.add('egifts-cross-sell-main-add-highlight');
      window.setTimeout(() => submit.classList.remove('egifts-cross-sell-main-add-highlight'), 1800);
    }, 420);
  };

  const closeGuardModal = (modal, resolve, value) => {
    if (!modal) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('egifts-product-cross-sell-modal-open');
    window.setTimeout(() => modal.remove(), 160);
    if (typeof resolve === 'function') resolve(value);
  };

  const showGuardModal = ({
    title,
    body = '',
    warning = '',
    primaryLabel = 'OK',
    primaryValue = 'primary',
    secondaryLabel = 'Anuluj',
    secondaryValue = 'cancel',
    primaryTone = 'primary',
    secondaryTone = 'secondary',
    showClose = true
  }) => new Promise((resolve) => {
    const modal = document.createElement('div');
    modal.className = 'egifts-product-cross-sell-modal';
    modal.dataset.egiftsProductCrossSellModal = '1';
    modal.innerHTML = `
      <div class="egifts-product-cross-sell-modal__backdrop" data-egifts-modal-close></div>
      <div class="egifts-product-cross-sell-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="EgiftsProductCrossSellModalTitle" tabindex="-1">
        ${showClose ? '<button type="button" class="egifts-product-cross-sell-modal__close" data-egifts-modal-close aria-label="Zamknij">×</button>' : ''}
        <span class="egifts-product-cross-sell-modal__icon" aria-hidden="true">!</span>
        <h3 id="EgiftsProductCrossSellModalTitle">${escapeHtml(title)}</h3>
        ${body ? `<p class="egifts-product-cross-sell-modal__text">${escapeHtml(body)}</p>` : ''}
        ${warning ? `<p class="egifts-product-cross-sell-modal__warning">${escapeHtml(warning)}</p>` : ''}
        <div class="egifts-product-cross-sell-modal__actions">
          <button type="button" class="egifts-product-cross-sell-modal__button is-${escapeHtml(primaryTone)}" data-egifts-modal-value="${escapeHtml(primaryValue)}">${escapeHtml(primaryLabel)}</button>
          ${secondaryLabel ? `<button type="button" class="egifts-product-cross-sell-modal__button is-${escapeHtml(secondaryTone)}" data-egifts-modal-value="${escapeHtml(secondaryValue)}">${escapeHtml(secondaryLabel)}</button>` : ''}
        </div>
      </div>`;

    let finished = false;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      finish('cancel');
    };
    const finish = (value) => {
      if (finished) return;
      finished = true;
      document.removeEventListener('keydown', onKeyDown, true);
      closeGuardModal(modal, resolve, value);
    };
    modal.addEventListener('click', (event) => {
      const action = event.target.closest?.('[data-egifts-modal-value]');
      if (action) {
        event.preventDefault();
        finish(String(action.dataset.egiftsModalValue || 'cancel'));
        return;
      }

      // X, przyciemnione tło albo dowolne miejsce poza białym oknem zamyka modal.
      // Kliknięcie wewnątrz samego dialogu niczego nie zamyka.
      const clickedClose = event.target.closest?.('[data-egifts-modal-close]');
      const clickedOutsideDialog = !event.target.closest?.('.egifts-product-cross-sell-modal__dialog');
      if (clickedClose || clickedOutsideDialog) {
        event.preventDefault();
        finish('cancel');
      }
    });

    document.addEventListener('keydown', onKeyDown, true);

    document.body.append(modal);
    document.documentElement.classList.add('egifts-product-cross-sell-modal-open');
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      modal.querySelector('.egifts-product-cross-sell-modal__dialog')?.focus();
    });
  });

  const showMainProductRequired = async (root) => {
    const choice = await showGuardModal({
      title: 'Najpierw dodaj ten produkt do koszyka',
      body: 'Żeby spersonalizować dodatkowy produkt bez utraty tego, co ustawiasz na tej karcie, najpierw dodaj oglądany teraz produkt do koszyka.',
      warning: 'Nic nie dodamy automatycznie. Po dodaniu produktu wróć tutaj i kliknij „Personalizuj” ponownie.',
      primaryLabel: 'Wróć i dodaj ten produkt',
      primaryValue: 'add-main',
      secondaryLabel: 'Zostań tutaj',
      secondaryValue: 'cancel'
    });
    if (choice === 'add-main') focusMainProductAdd(root);
    return false;
  };

  const confirmLeaveForCustomily = async (root, url, mainLine) => {
    const hasMain = Boolean(mainLine?.key);
    const first = await showGuardModal({
      title: 'Personalizacja otworzy się na osobnej karcie',
      body: hasMain
        ? 'Oglądany produkt jest już w koszyku, więc możesz bezpiecznie przejść do personalizacji dodatkowego produktu.'
        : 'Oglądany produkt nie został jeszcze dodany do koszyka. Jeśli chcesz go zachować, dodaj go przed przejściem dalej.',
      warning: 'WAŻNE: ta personalizacja wymaga opuszczenia obecnej karty produktu i przejścia na nową stronę.',
      primaryLabel: hasMain ? 'Przejdź do personalizacji' : 'Najpierw dodaj ten produkt',
      primaryValue: hasMain ? 'leave' : 'add-main',
      secondaryLabel: hasMain ? 'Zostań tutaj' : 'Przejdź mimo to',
      secondaryValue: hasMain ? 'cancel' : 'leave-anyway',
      secondaryTone: hasMain ? 'secondary' : 'danger'
    });

    if (first === 'add-main') {
      focusMainProductAdd(root);
      return false;
    }
    if (first === 'leave') {
      window.location.assign(url);
      return true;
    }
    if (first !== 'leave-anyway') return false;

    const second = await showGuardModal({
      title: 'Czy na pewno chcesz opuścić tę stronę?',
      body: 'Oglądany produkt nadal nie jest w koszyku.',
      warning: 'Po przejściu dalej możesz stracić ustawioną tutaj personalizację. Jeśli chcesz ten produkt, najbezpieczniej najpierw dodać go do koszyka.',
      primaryLabel: 'Zostań i dodaj produkt',
      primaryValue: 'add-main',
      secondaryLabel: 'Tak, przejdź bez dodania',
      secondaryValue: 'leave',
      secondaryTone: 'danger'
    });

    if (second === 'add-main') {
      focusMainProductAdd(root);
      return false;
    }
    if (second === 'leave') {
      window.location.assign(url);
      return true;
    }
    return false;
  };

  const confirmFallbackNavigation = async (root, url, mainLine) => {
    const hasMain = Boolean(mainLine?.key);
    const choice = await showGuardModal({
      title: 'Otworzyć kartę tego produktu?',
      body: hasMain
        ? 'Oglądany produkt jest już w koszyku. Możesz bezpiecznie otworzyć kartę dodatku.'
        : 'Ta akcja przeniesie Cię na inną kartę produktu.',
      warning: hasMain ? '' : 'Oglądany produkt nie jest jeszcze w koszyku. Przejście dalej może oznaczać utratę ustawień z tej strony.',
      primaryLabel: hasMain ? 'Przejdź dalej' : 'Najpierw dodaj ten produkt',
      primaryValue: hasMain ? 'leave' : 'add-main',
      secondaryLabel: hasMain ? 'Zostań tutaj' : 'Przejdź mimo to',
      secondaryValue: hasMain ? 'cancel' : 'leave-anyway',
      secondaryTone: hasMain ? 'secondary' : 'danger'
    });
    if (choice === 'add-main') {
      focusMainProductAdd(root);
      return false;
    }
    if (choice === 'leave') {
      window.location.assign(url);
      return true;
    }
    if (choice === 'leave-anyway') {
      return confirmLeaveForCustomily(root, url, null);
    }
    return false;
  };

  const armIndependentNativeAdd = () => {
    nativeState.independentAddUntil = Date.now() + 8000;
  };

  const shouldMakeNativeAddIndependent = (url = '') => (
    Date.now() < Number(nativeState.independentAddUntil || 0)
    && /\/cart\/add(?:\.js)?(?:\?|$)/.test(String(url || ''))
  );

  const isParentFieldName = (name = '') => {
    const key = String(name || '');
    return /(^|\[)(parent_line_key|parent_id)(\]|$)/.test(key)
      || key.includes('_parent_customization_id');
  };

  const stripParentFromProperties = (properties) => {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      return { value: properties, changed: false };
    }
    const value = { ...properties };
    let changed = false;
    if (Object.prototype.hasOwnProperty.call(value, '_parent_customization_id')) {
      delete value._parent_customization_id;
      changed = true;
    }
    return { value, changed };
  };

  const stripParentFromCartItem = (item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return { value: item, changed: false };
    const value = { ...item };
    let changed = false;
    if (Object.prototype.hasOwnProperty.call(value, 'parent_line_key')) {
      delete value.parent_line_key;
      changed = true;
    }
    if (Object.prototype.hasOwnProperty.call(value, 'parent_id')) {
      delete value.parent_id;
      changed = true;
    }
    const properties = stripParentFromProperties(value.properties);
    if (properties.changed) {
      value.properties = properties.value;
      changed = true;
    }
    return { value, changed };
  };

  const stripParentFromJsonPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return { value: payload, changed: false };
    if (Array.isArray(payload)) {
      let changed = false;
      const value = payload.map((item) => {
        const result = stripParentFromCartItem(item);
        changed = changed || result.changed;
        return result.value;
      });
      return { value, changed };
    }
    const value = { ...payload };
    let changed = false;
    if (Array.isArray(value.items)) {
      value.items = value.items.map((item) => {
        const result = stripParentFromCartItem(item);
        changed = changed || result.changed;
        return result.value;
      });
    } else {
      return stripParentFromCartItem(value);
    }
    return { value, changed };
  };

  const stripParentFromParams = (params) => {
    if (!params || typeof params.entries !== 'function') return { value: params, changed: false };
    const keys = [];
    for (const [key] of params.entries()) if (isParentFieldName(key)) keys.push(key);
    if (!keys.length) return { value: params, changed: false };
    keys.forEach((key) => params.delete(key));
    return { value: params, changed: true };
  };

  const rewriteIndependentCartAddBody = (body) => {
    if (body == null) return { body, changed: false };
    if (typeof body === 'string') {
      const trimmed = body.trim();
      if (!trimmed) return { body, changed: false };
      try {
        const parsed = JSON.parse(trimmed);
        const result = stripParentFromJsonPayload(parsed);
        return result.changed ? { body: JSON.stringify(result.value), changed: true } : { body, changed: false };
      } catch (error) {}
      try {
        const params = new URLSearchParams(body);
        const result = stripParentFromParams(params);
        return result.changed ? { body: params.toString(), changed: true } : { body, changed: false };
      } catch (error) {
        return { body, changed: false };
      }
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const clone = new FormData();
      let changed = false;
      for (const [key, value] of body.entries()) {
        if (isParentFieldName(key)) {
          changed = true;
          continue;
        }
        clone.append(key, value);
      }
      return changed ? { body: clone, changed: true } : { body, changed: false };
    }
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      const clone = new URLSearchParams(body.toString());
      const result = stripParentFromParams(clone);
      return { body: result.value, changed: result.changed };
    }
    return { body, changed: false };
  };

  const installNativeXhrBridge = () => {
    if (nativeState.xhrBridgeInstalled || typeof XMLHttpRequest === 'undefined') return;
    nativeState.xhrBridgeInstalled = true;
    const proto = XMLHttpRequest.prototype;
    const originalOpen = proto.open;
    const originalSend = proto.send;
    proto.open = function(method, url, ...rest) {
      this.__egiftsProductCrossSellUrl = String(url || '');
      return originalOpen.call(this, method, url, ...rest);
    };
    proto.send = function(body) {
      const url = String(this.__egiftsProductCrossSellUrl || '');
      if (!shouldMakeNativeAddIndependent(url)) return originalSend.call(this, body);
      const rewritten = rewriteIndependentCartAddBody(body);
      nativeState.independentAddUntil = 0;
      return originalSend.call(this, rewritten.body);
    };
  };

  const deactivateNativeBridge = () => {
    nativeState.active = false;
    nativeState.parentProductId = '';
    nativeState.targetGid = '';
    nativeState.expiresAt = 0;
  };

  const installNativeFetchBridge = () => {
    installNativeXhrBridge();
    if (window.fetch === nativeState.fetchBridge) return;
    const sourceFetch = window.fetch.bind(window);
    const bridge = async (input, init) => {
      const url = typeof input === 'string' ? input : String(input?.url || '');

      if (shouldMakeNativeAddIndependent(url)) {
        let nextInput = input;
        let nextInit = init ? { ...init } : {};
        let rewritten = { body: nextInit.body, changed: false };
        if (Object.prototype.hasOwnProperty.call(nextInit, 'body')) {
          rewritten = rewriteIndependentCartAddBody(nextInit.body);
          if (rewritten.changed) nextInit.body = rewritten.body;
        } else if (typeof Request !== 'undefined' && input instanceof Request) {
          try {
            const originalText = await input.clone().text();
            rewritten = rewriteIndependentCartAddBody(originalText);
            if (rewritten.changed) nextInput = new Request(input, { body: rewritten.body });
          } catch (error) {}
        }
        nativeState.independentAddUntil = 0;
        return sourceFetch(nextInput, nextInit);
      }

      const body = typeof init?.body === 'string' ? init.body : '';
      let shouldOverride = false;
      if (
        nativeState.active
        && Date.now() < nativeState.expiresAt
        && nativeState.targetGid
        && url.includes('/graphql.json')
        && body.includes('query getProductById')
        && body.includes('crossSellProductId')
        && body.includes('crossSellCollectionIdsJson')
      ) {
        try {
          const payload = JSON.parse(body);
          const requestedId = String(payload?.variables?.id || '');
          const expectedId = `gid://shopify/Product/${nativeState.parentProductId}`;
          shouldOverride = !nativeState.parentProductId || requestedId === expectedId;
        } catch (error) {
          shouldOverride = false;
        }
      }

      const response = await sourceFetch(input, init);
      if (!shouldOverride) return response;
      try {
        const json = await response.clone().json();
        if (!json?.data?.product) {
          deactivateNativeBridge();
          return response;
        }
        const targetGid = nativeState.targetGid;
        deactivateNativeBridge();
        json.data.product.crossSellProductId = { value: targetGid };
        json.data.product.crossSellCollectionIdsJson = { value: '[]' };
        const headers = new Headers(response.headers);
        headers.set('content-type', 'application/json; charset=utf-8');
        headers.delete('content-length');
        headers.delete('content-encoding');
        return new Response(JSON.stringify(json), {
          status: response.status,
          statusText: response.statusText,
          headers
        });
      } catch (error) {
        deactivateNativeBridge();
        return response;
      }
    };
    nativeState.fetchBridge = bridge;
    window.fetch = bridge;
  };

  const createNativeLauncher = (root, itemKey) => {
    const host = root.querySelector('[data-egifts-product-cross-sell-native-host]');
    if (!host) return null;
    host.innerHTML = '';
    const launcher = document.createElement('div');
    launcher.className = 'egifts-product-cross-sell__native-launcher tee-edit-cross-sell-product';
    launcher.dataset.egiftsProductCrossSellNativeLauncher = '1';
    launcher.dataset.itemKey = String(itemKey || '');
    launcher.setAttribute('aria-hidden', 'true');
    host.append(launcher);
    document.dispatchEvent(new Event('shopify-event-cart-ajax'));
    return launcher;
  };

  const findNativeTrigger = (launcher) => {
    const wrapper = launcher?.querySelector('.tee-edit-cross-sell-product__wrapper');
    if (!wrapper) return null;
    return wrapper.querySelector('.tee-cross-sell-product-button, button, [role="button"], a[href]')
      || (!wrapper.firstElementChild?.matches('.tee-spinner, .tee-spinner *') ? wrapper.firstElementChild : null);
  };

  const waitForNativeTrigger = async (launcher) => {
    for (let attempt = 0; attempt < 70; attempt += 1) {
      const trigger = findNativeTrigger(launcher);
      if (trigger) return trigger;
      if (attempt === 10 || attempt === 30 || attempt === 50) {
        document.dispatchEvent(new Event('shopify-event-cart-ajax'));
      }
      await wait(100);
    }
    return null;
  };

  const nativePopupIsOpen = () => {
    const popup = document.querySelector('.tee-cross-sell-product-popup');
    if (!popup) return false;
    const style = window.getComputedStyle(popup);
    return style.display !== 'none' && style.visibility !== 'hidden' && popup.getClientRects().length > 0;
  };

  const teeArtworkHasContent = (svg) => Boolean(svg && svg.querySelector('image, path, text, tspan, use, foreignObject, g'));

  const serializeVisibleTeeArtwork = () => {
    const popup = document.querySelector('.tee-cross-sell-product-popup[data-egifts-product-cross-sell-owned="1"]')
      || (nativePopupIsOpen() ? document.querySelector('.tee-cross-sell-product-popup') : null);
    const isOurRenderedClone = (svg) => Boolean(svg.closest(
      '[data-egifts-cart-artwork], [data-egifts-artwork-layer], .egifts-cart-sd-preview, .egifts-cart-sd-panel'
    ));
    const popupCandidates = popup
      ? Array.from(popup.querySelectorAll('svg.tee-mockup-artwork')).filter(teeArtworkHasContent)
      : [];
    const globalCandidates = popupCandidates.length
      ? []
      : Array.from(document.querySelectorAll('svg.tee-mockup-artwork'))
          .filter(teeArtworkHasContent)
          .filter((svg) => !isOurRenderedClone(svg));
    const allCandidates = popupCandidates.length ? popupCandidates : globalCandidates;
    if (!allCandidates.length) return '';
    let candidates = allCandidates.filter((svg) => {
      const style = window.getComputedStyle(svg);
      const rect = svg.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
    });
    if (!candidates.length) candidates = allCandidates;
    candidates.sort((a, b) => {
      const first = a.getBoundingClientRect();
      const second = b.getBoundingClientRect();
      return second.width * second.height - first.width * first.height;
    });
    const source = candidates[0];
    const clone = source.cloneNode(true);
    clone.querySelectorAll('script').forEach((node) => node.remove());
    [clone, ...clone.querySelectorAll('*')].forEach((node) => {
      Array.from(node.attributes || []).forEach((attribute) => {
        if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name);
      });
    });
    if (!clone.getAttribute('viewBox')) {
      try {
        const box = source.getBBox();
        if (box.width > 0 && box.height > 0) clone.setAttribute('viewBox', `${box.x} ${box.y} ${box.width} ${box.height}`);
      } catch (error) {}
    }
    clone.removeAttribute('width');
    clone.removeAttribute('height');
    clone.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    try {
      return new XMLSerializer().serializeToString(clone);
    } catch (error) {
      return '';
    }
  };

  const captureNativeArtworkNow = () => {
    const artwork = serializeVisibleTeeArtwork();
    if (artwork) nativeState.artwork = artwork;
    return artwork;
  };

  const resetNativeArtworkState = () => {
    if (nativeState.artworkTimer) window.clearInterval(nativeState.artworkTimer);
    nativeState.artworkTimer = null;
    nativeState.artwork = '';
    nativeState.targetProductId = '';
    nativeState.targetTitle = '';
    nativeState.targetRoot = null;
    nativeState.baselineCustomizations = new Set();
  };

  const startNativeArtworkCapture = (baselineCart, productData, root) => {
    resetNativeArtworkState();
    nativeState.targetProductId = String(productData?.productId || '').replace(/\D/g, '');
    nativeState.targetTitle = String(productData?.title || '');
    nativeState.targetRoot = root;
    nativeState.baselineCustomizations = new Set(
      (baselineCart?.items || []).map((item) => String(item?.properties?._customization_id || '')).filter(Boolean)
    );
    captureNativeArtworkNow();
    nativeState.artworkTimer = window.setInterval(captureNativeArtworkNow, 120);
    window.setTimeout(() => {
      if (nativeState.artworkTimer) resetNativeArtworkState();
    }, 180000);
  };

  const persistNativeArtworkCore = async () => {
    if (!nativeState.artworkTimer && !nativeState.targetProductId) return null;
    const finalArtwork = serializeVisibleTeeArtwork();
    if (finalArtwork) nativeState.artwork = finalArtwork;
    for (let attempt = 0; attempt < 60; attempt += 1) {
      try {
        const cart = await fetchCart();
        const newItems = (cart?.items || []).filter((candidate) => {
          const customizationId = String(candidate?.properties?._customization_id || '');
          return customizationId && !nativeState.baselineCustomizations.has(customizationId);
        });
        const item = newItems.find((candidate) => {
          const productId = String(candidate?.product_id || '').replace(/\D/g, '');
          return !nativeState.targetProductId || productId === nativeState.targetProductId;
        }) || newItems[0];
        const customizationId = String(item?.properties?._customization_id || '');
        if (customizationId) {
          const artwork = nativeState.artwork || captureNativeArtworkNow();
          if (artwork) {
            try { window.localStorage.setItem(`egifts-sd-artwork:${customizationId}`, artwork); } catch (error) {}
          }
          return item;
        }
      } catch (error) {}
      await wait(120);
    }
    return null;
  };

  const persistNativeArtworkAndNotify = () => {
    if (nativeState.persistPromise) return nativeState.persistPromise;
    const root = nativeState.targetRoot;
    const title = nativeState.targetTitle;
    nativeState.persistPromise = persistNativeArtworkCore()
      .then(async (item) => {
        if (!item) return null;
        resetNativeArtworkState();
        await updateCartBubble();
        if (root) {
          showAddedToast(root, title || 'Produkt personalizowany');
          setStatus(root, 'Produkt został dodany do koszyka. Możesz dobrać kolejny.');
        }
        document.dispatchEvent(new CustomEvent('shopify-event-cart-modified', { detail: { source: 'egifts-product-cross-sell-teeinblue' } }));
        return item;
      })
      .finally(() => { nativeState.persistPromise = null; });
    return nativeState.persistPromise;
  };

  const openNativeTeeinblue = async (root, cardButton, mainLine, productData) => {
    if (nativeState.opening) return;
    nativeState.opening = true;
    setCardLoading(cardButton, true, 'Ładuję kreator...');
    try {
      if (!mainLine?.key || !productData?.productGid) throw new Error('Brak danych do uruchomienia personalizacji.');
      installNativeFetchBridge();

      // Bridge uzbrajamy przed wstawieniem launchera, bo część wersji SDK
      // pobiera dane cross-sellu natychmiast po wykryciu nowego elementu DOM.
      nativeState.active = true;
      nativeState.parentProductId = currentProductId(root);
      nativeState.targetGid = String(productData.productGid);
      nativeState.expiresAt = Date.now() + 10000;

      const launcher = createNativeLauncher(root, mainLine.key);
      if (!launcher) throw new Error('Brak launchera personalizacji.');

      const trigger = await waitForNativeTrigger(launcher);
      if (!trigger) throw new Error('Teeinblue nie przygotował kreatora.');

      let baselineCart;
      try { baselineCart = await fetchCart(); } catch (error) { baselineCart = { items: [] }; }
      startNativeArtworkCapture(baselineCart, productData, root);

      trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
      let opened = false;
      for (let attempt = 0; attempt < 120; attempt += 1) {
        if (nativePopupIsOpen()) {
          opened = true;
          break;
        }
        await wait(100);
      }
      if (!opened) throw new Error('Nie udało się otworzyć kreatora Teeinblue.');
      const popup = document.querySelector('.tee-cross-sell-product-popup');
      if (popup) popup.dataset.egiftsProductCrossSellOwned = '1';
      setCardLoading(cardButton, false);
      setStatus(root, 'Główny produkt jest już w koszyku. Dokończ personalizację dodatku.');
      return true;
    } catch (error) {
      deactivateNativeBridge();
      if (nativeState.artworkTimer) resetNativeArtworkState();
      setCardLoading(cardButton, false);
      console.warn('[E-GIFTS] Product page Teeinblue cross-sell:', error);
      return false;
    } finally {
      nativeState.opening = false;
    }
  };

  const updateCartBubble = async () => {
    try {
      const cart = await fetch(route('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json());
      const anchor = document.querySelector('#cart-icon-bubble');
      if (!anchor) return;
      let bubble = anchor.querySelector('.cart-count-bubble');
      if (Number(cart.item_count || 0) < 1) {
        bubble?.remove();
        return;
      }
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        anchor.append(bubble);
      }
      bubble.innerHTML = `<span aria-hidden="true">${Number(cart.item_count || 0)}</span><span class="visually-hidden">${Number(cart.item_count || 0)}</span>`;
    } catch (error) {}
  };

  const addDirectly = async (root, button) => {
    const variantId = String(button.dataset.productVariantId || '');
    if (!variantId) throw new Error('Nie udało się wybrać wariantu produktu.');
    setCardLoading(button, true, 'Dodaję dodatek...');
    const response = await fetch(route('cart/add.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.description || 'Nie udało się dodać produktu.');
    }
    const productTitle = String(button.dataset.productTitle || '');
    setCardLoading(button, true, 'Dodano do koszyka ✓');
    setStatus(root, 'Dodano wybrany produkt do koszyka. Możesz dobrać kolejny.');
    showAddedToast(root, productTitle);
    await updateCartBubble();
    document.dispatchEvent(new CustomEvent('shopify-event-cart-modified', { detail: { source: 'egifts-product-cross-sell' } }));
    window.setTimeout(() => setCardLoading(button, false), 1800);
  };

  const openProduct = async (root, button) => {
    if (!button || button.disabled) return;
    const handle = String(button.dataset.productHandle || '');
    const url = String(button.dataset.productUrl || route(`products/${handle}`));
    const directAdd = button.dataset.productDirectAdd === 'true';
    const personalizer = String(button.dataset.productPersonalizer || '');

    try {
      setStatus(root, '');

      // Zwykły dodatek może zostać dodany AJAX-em bez opuszczania bieżącej strony.
      // Nie dodajemy automatycznie produktu głównego.
      if (directAdd) {
        await addDirectly(root, button);
        return;
      }

      const mainLine = await getSessionMainLine(root);

      if (personalizer === 'teeinblue' && handle) {
        if (!mainLine) {
          await confirmLeaveForCustomily(root, url, null);
          return;
        }

        setCardLoading(button, true, 'Przygotowuję kreator...');
        const data = await getProductData(handle);
        const opened = await openNativeTeeinblue(root, button, mainLine, data);
        if (opened) return;

        // Nie przenosimy klienta automatycznie. Jeżeli natywny popup nie wystartuje,
        // klient sam decyduje, czy chce otworzyć osobną kartę produktu.
        setStatus(root, 'Nie udało się otworzyć kreatora na tej stronie.');
        await confirmFallbackNavigation(root, url, mainLine);
        return;
      }

      if (personalizer === 'customily' && url) {
        await confirmLeaveForCustomily(root, url, mainLine);
        return;
      }

      if (url) {
        await confirmFallbackNavigation(root, url, mainLine);
      }
    } catch (error) {
      setCardLoading(button, false, 'Spróbuj ponownie');
      setStatus(root, error?.message || 'Nie udało się wykonać tej operacji.');
      window.setTimeout(() => setCardLoading(button, false), 2200);
    }
  };

  const loadRoot = async (root) => {
    if (root.dataset.egiftsProductCrossSellLoading === 'true') return;
    root.dataset.egiftsProductCrossSellLoading = 'true';
    try {
      const prepared = await prepareRandomPool(root);
      if (!prepared.pool.length) throw new Error('Brak produktów do pokazania.');
      setPool(root, 'random', prepared.pool, 'random');
      if (!root._egiftsProductCrossSellActive || root._egiftsProductCrossSellActive === 'random') {
        activateCategory(root, 'random');
        renderActive(root, false);
      } else {
        updateShuffleButton(root);
      }
      root.dataset.egiftsProductCrossSellReady = 'true';
      hydrateRandomPool(root, prepared).catch(() => {});
    } catch (error) {
      console.warn('[E-GIFTS] Product cross-sell:', error);
      root.hidden = true;
    } finally {
      delete root.dataset.egiftsProductCrossSellLoading;
    }
  };

  const observeRoot = (root) => {
    if (observedRoots.has(root)) return;
    observedRoots.add(root);
    delete root.dataset.egiftsProductCrossSellLoading;
    root.dataset.egiftsProductCrossSellObserved = 'true';
    if (!('IntersectionObserver' in window)) {
      loadRoot(root);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      loadRoot(root);
    }, { rootMargin: '500px 0px' });
    observer.observe(root);
  };

  const initAll = (scope = document) => {
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));
    if (scope.matches?.(ROOT_SELECTOR)) roots.unshift(scope);
    roots.forEach((root) => {
      if (!root.isConnected) return;
      const key = String(root.dataset.currentSectionId || root.dataset.currentProductId || root.dataset.currentProductHandle || 'main');
      const existing = liveRoots.get(key);
      if (existing?.isConnected && existing !== root) {
        if (existing.contains(root) || existing.getClientRects().length) {
          root.remove();
          return;
        }
        existing.remove();
      }
      liveRoots.set(key, root);
      primeMainBaseline(root).catch(() => {});
      observeRoot(root);
    });
  };
  const rootObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && (node.matches(ROOT_SELECTOR) || node.querySelector(ROOT_SELECTOR))) {
          initAll(node);
        }
      });
    });
  });
  const watchRoots = () => {
    rootObserver.observe(document.body, { childList: true, subtree: true });
  };
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchRoots, { once: true });
  } else {
    watchRoots();
  }


  document.addEventListener('click', (event) => {
    const categoryShuffle = event.target.closest?.('[data-egifts-product-cross-sell-category-shuffle]');
    if (categoryShuffle) {
      const root = categoryShuffle.closest(ROOT_SELECTOR);
      if (!root || categoryShuffle.disabled) return;
      event.preventDefault();
      renderActive(root, true);
      return;
    }

    const shuffle = event.target.closest?.('[data-egifts-product-cross-sell-shuffle]');
    if (shuffle) {
      const root = shuffle.closest(ROOT_SELECTOR);
      if (!root || shuffle.disabled) return;
      event.preventDefault();
      activateCategory(root, 'random');
      renderActive(root, true);
      return;
    }

    const category = event.target.closest?.('[data-egifts-product-cross-sell-category]');
    if (category) {
      const root = category.closest(ROOT_SELECTOR);
      if (!root) return;
      event.preventDefault();
      const key = String(category.dataset.egiftsProductCrossSellCategory || 'random');
      const states = ensureStates(root);
      activateCategory(root, key);
      setStatus(root, '');

      if (states.has(key)) {
        renderActive(root, false);
        return;
      }
      if (key === 'random') return;

      const requestId = Number(root._egiftsProductCrossSellRequestId || 0) + 1;
      root._egiftsProductCrossSellRequestId = requestId;
      category.disabled = true;

      loadCategory(root, key)
        .then((products) => {
          if (!products.length) {
            if (root._egiftsProductCrossSellActive === key) setStatus(root, 'W tej kategorii nie znaleźliśmy teraz dostępnych produktów.');
            return;
          }
          const mode = key === 'newest' || (CATEGORY_COLLECTIONS[key] || []).length > 1 ? 'ordered' : 'random';
          setPool(root, key, products, mode);
          if (root._egiftsProductCrossSellActive === key && root._egiftsProductCrossSellRequestId === requestId) {
            renderActive(root, false);
          }
        })
        .catch(() => {
          if (root._egiftsProductCrossSellActive === key && root._egiftsProductCrossSellRequestId === requestId) {
            setStatus(root, 'Nie udało się wczytać tej kategorii. Spróbuj ponownie.');
          }
        })
        .finally(() => { category.disabled = false; });
      return;
    }

    const previous = event.target.closest?.('[data-egifts-product-cross-sell-prev]');
    const next = event.target.closest?.('[data-egifts-product-cross-sell-next]');
    if (previous || next) {
      const root = (previous || next).closest(ROOT_SELECTOR);
      if (!root) return;
      event.preventDefault();
      scrollCarousel(root, next ? 1 : -1);
      return;
    }

    const open = event.target.closest?.('[data-egifts-product-cross-sell-open]');
    if (open) {
      const root = open.closest(ROOT_SELECTOR);
      if (!root) return;
      event.preventDefault();
      openProduct(root, open);
    }
  });

  document.addEventListener('pointerdown', (event) => {
    const popup = event.target.closest?.('.tee-cross-sell-product-popup[data-egifts-product-cross-sell-owned="1"]');
    if (!popup) return;
    const atc = event.target.closest?.('.tee-btn--atc, [class*="tee-btn"][class*="atc"], button[type="submit"]');
    if (!atc) return;
    armIndependentNativeAdd();
    captureNativeArtworkNow();
    window.setTimeout(captureNativeArtworkNow, 30);
    window.setTimeout(captureNativeArtworkNow, 90);
    window.setTimeout(persistNativeArtworkAndNotify, 40);
  }, true);

  document.addEventListener('click', (event) => {
    const popup = event.target.closest?.('.tee-cross-sell-product-popup[data-egifts-product-cross-sell-owned="1"]');
    if (!popup) return;
    if (event.target.closest?.('.tee-btn--atc, [class*="tee-btn"][class*="atc"], button[type="submit"]')) {
      armIndependentNativeAdd();
      captureNativeArtworkNow();
    }
  }, true);

  document.addEventListener('teeinblue-event-after-cart-added', persistNativeArtworkAndNotify);
  document.addEventListener('teeinblue:added-to-cart', persistNativeArtworkAndNotify);
  document.addEventListener('tee:added-to-cart', persistNativeArtworkAndNotify);

  window.EgiftsProductCrossSell = { version: VERSION, initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }
})();